/**
 * Weekly availability grid; cells use containment so overlapping hour slots do not expand.
 */

import * as React from 'react';
import { Clock, CheckCircle, ChevronDown, ChevronUp, ChevronRight, Maximize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WeeklyAvailabilitySlot } from '@/types/user.types';
import '@/styles/components/availability-grid.css';

export type { WeeklyAvailabilitySlot };

/** Props for {@link AvailabilityGrid}. */
interface AvailabilityGridProps {
  value?: { weekly: WeeklyAvailabilitySlot[] };
  onChange?: (value: { weekly: WeeklyAvailabilitySlot[] }) => void;
  disabled?: boolean;
  className?: string;
  maxVisibleRows?: number;
  allowedSlots?: { start: string; end: string }[];
}

/** Weekdays shown on the grid (Mon–Fri). */
const DAYS = [
  { value: 1, label: 'Lundi', short: 'Lun' },
  { value: 2, label: 'Mardi', short: 'Mar' },
  { value: 3, label: 'Mercredi', short: 'Mer' },
  { value: 4, label: 'Jeudi', short: 'Jeu' },
  { value: 5, label: 'Vendredi', short: 'Ven' },
];

/** Zero-pads a number to two digits for HH:MM labels. */
function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/** Build 30-min-start, 1-hour-span slots from 08:00 through 20:30. */
function generateTimeSlots(): { label: string; start: string; end: string }[] {
  const slots = [];
  for (let startMinutes = 8 * 60; startMinutes <= 19 * 60 + 30; startMinutes += 30) {
    const endMinutes = startMinutes + 60;
    const startH = Math.floor(startMinutes / 60);
    const startM = startMinutes % 60;
    const endH = Math.floor(endMinutes / 60);
    const endM = endMinutes % 60;
    const start = `${pad(startH)}:${pad(startM)}`;
    const end = `${pad(endH)}:${pad(endM)}`;
    slots.push({ label: `${start}-${end}`, start, end });
  }
  return slots;
}

/** Fixed 30-minute-start hourly slots from 08:00 through 20:30. */
const TIME_SLOTS = generateTimeSlots();

/** Slot-label to selected map for one weekday. */
type DayAvailability = { [timeSlotLabel: string]: boolean };
/** Weekday index to that day's slot selection. */
type GridData = { [day: number]: DayAvailability };

/** Parse `HH:MM` as minutes from midnight. */
const timeToMinutes = (time: string | undefined): number => {
  if (!time || typeof time !== 'string') return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

/** True when two `[start, end)` ranges overlap. */
const checkOverlap = (
  slotStart: string,
  slotEnd: string,
  fixedStart: string,
  fixedEnd: string,
): boolean => {
  const slotStartMin = timeToMinutes(slotStart);
  const slotEndMin = timeToMinutes(slotEnd);
  const fixedStartMin = timeToMinutes(fixedStart);
  const fixedEndMin = timeToMinutes(fixedEnd);
  return slotStartMin < fixedEndMin && slotEndMin > fixedStartMin;
};

/**
 * A grid cell (a 1-hour window) is only "checked" when it is fully contained
 * within a stored slot. Using containment (not overlap) is essential: the grid's
 * time slots start every 30 min but each spans 1 hour, so they overlap each other.
 * With overlap, a single stored 08:00–09:00 slot would also light up 08:30–09:30,
 * causing selections to expand on their own on every re-sync.
 */
const isCellWithinSlot = (
  cellStart: string,
  cellEnd: string,
  slotStart: string,
  slotEnd: string,
): boolean =>
  timeToMinutes(cellStart) >= timeToMinutes(slotStart) &&
  timeToMinutes(cellEnd) <= timeToMinutes(slotEnd);

/** Project stored slots onto the overlapping hour grid. */
const slotsToGrid = (slots: WeeklyAvailabilitySlot[]): GridData => {
  const grid: GridData = {};
  slots.forEach((slot) => {
    if (slot?.start == null || slot.end == null) return;
    if (!grid[slot.day]) grid[slot.day] = {};
    TIME_SLOTS.forEach((timeSlot) => {
      if (isCellWithinSlot(timeSlot.start, timeSlot.end, slot.start, slot.end)) {
        grid[slot.day][timeSlot.label] = true;
      }
    });
  });
  return grid;
};

/** Stable serialization of a normalized slot list, for equality checks. */
const serializeSlots = (slots: WeeklyAvailabilitySlot[]): string =>
  slots
    .map((s) => `${s.day}:${s.start}-${s.end}`)
    .sort((a, b) => a.localeCompare(b))
    .join('|');

/** Merge contiguous checked cells back into weekly slots. */
const gridToSlots = (grid: GridData): WeeklyAvailabilitySlot[] => {
  const slots: WeeklyAvailabilitySlot[] = [];
  Object.keys(grid).forEach((dayStr) => {
    const day = Number(dayStr);
    const dayData = grid[day];
    const checkedSlots: (typeof TIME_SLOTS)[0][] = [];
    TIME_SLOTS.forEach((timeSlot) => {
      if (dayData[timeSlot.label]) checkedSlots.push(timeSlot);
    });
    if (checkedSlots.length === 0) return;
    let currentGroup: (typeof TIME_SLOTS)[0][] = [checkedSlots[0]];
    for (let i = 1; i < checkedSlots.length; i++) {
      const prevSlot = checkedSlots[i - 1];
      const currentSlot = checkedSlots[i];
      if (timeToMinutes(currentSlot.start) <= timeToMinutes(prevSlot.end)) {
        currentGroup.push(currentSlot);
      } else {
        slots.push({ day, start: currentGroup[0].start, end: currentGroup.at(-1)!.end });
        currentGroup = [currentSlot];
      }
    }
    slots.push({ day, start: currentGroup[0].start, end: currentGroup.at(-1)!.end });
  });
  return slots;
};

/** Weekly availability grid; cells use containment so overlapping hour slots do not expand. */
export const AvailabilityGrid: React.FC<AvailabilityGridProps> = ({
  value = { weekly: [] },
  onChange,
  disabled = false,
  className,
  maxVisibleRows,
  allowedSlots,
}) => {
  const effectiveTimeSlots = React.useMemo(() => {
    if (!allowedSlots || allowedSlots.length === 0) return TIME_SLOTS;
    const minStart = Math.min(...allowedSlots.map((s) => timeToMinutes(s.start)));
    return TIME_SLOTS.filter((ts) => timeToMinutes(ts.start) >= minStart);
  }, [allowedSlots]);

  const visibleSlots = React.useMemo(() => {
    if (!allowedSlots || allowedSlots.length === 0) return effectiveTimeSlots;
    return effectiveTimeSlots.filter((ts) =>
      allowedSlots.some((allowed) => checkOverlap(ts.start, ts.end, allowed.start, allowed.end)),
    );
  }, [allowedSlots, effectiveTimeSlots]);

  const [gridData, setGridData] = React.useState<GridData>(() => slotsToGrid(value.weekly || []));
  const [showAll, setShowAll] = React.useState(false);
  const isInternalUpdate = React.useRef(false);
  const prevAllowedSlotsRef = React.useRef<typeof allowedSlots>(allowedSlots);

  // Re-sync the grid only when the incoming value actually represents a different
  // selection than what the grid currently shows. `value` is a new object on every
  // parent render (e.g. when another field like subject changes), so comparing by
  // reference would needlessly rebuild the grid and re-expand selections.
  React.useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const incomingGrid = slotsToGrid(value.weekly || []);
    const incomingNormalized = serializeSlots(gridToSlots(incomingGrid));
    const currentNormalized = serializeSlots(gridToSlots(gridData));
    if (incomingNormalized !== currentNormalized) {
      setGridData(incomingGrid);
    }
  }, [value, gridData]);

  // When allowedSlots change (user switches programs), deselect slots no longer allowed.
  // Skip clearing when transitioning from undefined/empty to a defined set (async load).
  React.useEffect(() => {
    const prevAllowedSlots = prevAllowedSlotsRef.current;
    prevAllowedSlotsRef.current = allowedSlots;

    // If allowedSlots is undefined or empty, no restriction → don't clear anything
    if (!allowedSlots || allowedSlots.length === 0 || !onChange) return;

    // Don't clear if this is the first time allowedSlots becomes defined (async load scenario)
    if (!prevAllowedSlots || prevAllowedSlots.length === 0) return;

    const currentSlots = value.weekly ?? [];
    const filtered = currentSlots.filter((slot) =>
      allowedSlots.some((allowed) =>
        checkOverlap(slot.start, slot.end, allowed.start, allowed.end),
      ),
    );

    if (filtered.length !== currentSlots.length) {
      setGridData(slotsToGrid(filtered));
      isInternalUpdate.current = true;
      onChange({ weekly: filtered });
    }
  }, [allowedSlots]);

  /** Toggle one cell and emit merged weekly slots. */
  const handleCellClick = (day: number, timeSlot: (typeof TIME_SLOTS)[0]) => {
    if (disabled) return;
    const newGrid = { ...gridData };
    const isCurrentlyChecked = gridData[day]?.[timeSlot.label];
    if (!newGrid[day]) newGrid[day] = {};
    newGrid[day][timeSlot.label] = !isCurrentlyChecked;
    setGridData(newGrid);
    isInternalUpdate.current = true;
    onChange?.({ weekly: gridToSlots(newGrid) });
  };

  /** Whether that day/slot cell is selected. */
  const isCellChecked = (day: number, timeSlot: (typeof TIME_SLOTS)[0]): boolean =>
    gridData[day]?.[timeSlot.label] || false;

  const isRowAllSelected = (timeSlot: (typeof TIME_SLOTS)[0]): boolean =>
    DAYS.every((d) => gridData[d.value]?.[timeSlot.label]);

  const isColAllSelected = (day: number): boolean =>
    visibleSlots.every((ts) => gridData[day]?.[ts.label]);

  const isAllSelected = (): boolean =>
    DAYS.every((d) => visibleSlots.every((ts) => gridData[d.value]?.[ts.label]));

  /** Toggle every weekday for one time slot. */
  const handleSelectRow = (timeSlot: (typeof TIME_SLOTS)[0]) => {
    if (disabled) return;
    const allChecked = isRowAllSelected(timeSlot);
    const newGrid = { ...gridData };
    DAYS.forEach((d) => {
      if (!newGrid[d.value]) newGrid[d.value] = {};
      newGrid[d.value][timeSlot.label] = !allChecked;
    });
    setGridData(newGrid);
    isInternalUpdate.current = true;
    onChange?.({ weekly: gridToSlots(newGrid) });
  };

  /** Toggle every visible slot for one weekday. */
  const handleSelectCol = (day: number) => {
    if (disabled) return;
    const allChecked = isColAllSelected(day);
    const newGrid = { ...gridData };
    if (!newGrid[day]) newGrid[day] = {};
    visibleSlots.forEach((ts) => {
      newGrid[day][ts.label] = !allChecked;
    });
    setGridData(newGrid);
    isInternalUpdate.current = true;
    onChange?.({ weekly: gridToSlots(newGrid) });
  };

  /** Toggle the entire visible grid. */
  const handleSelectAll = () => {
    if (disabled) return;
    const allChecked = isAllSelected();
    const newGrid: GridData = {};
    DAYS.forEach((d) => {
      newGrid[d.value] = {};
      visibleSlots.forEach((ts) => {
        newGrid[d.value][ts.label] = !allChecked;
      });
    });
    setGridData(newGrid);
    isInternalUpdate.current = true;
    onChange?.({ weekly: gridToSlots(newGrid) });
  };

  /** Native tooltip for a cell (empty when disabled). */
  const getCellTitle = (isChecked: boolean): string => {
    if (disabled) return '';
    return isChecked
      ? 'Créneau sélectionné - Cliquer pour désélectionner'
      : 'Cliquer pour sélectionner ce créneau';
  };

  return (
    <div className={cn('availability-grid-container', className)}>
      <div className="availability-section">
        <div className="availability-legend">
          <div className="availability-legend-item">
            <div className="availability-legend-color availability-legend-checked"></div>
            <span>Disponible</span>
          </div>
          <div className="availability-legend-item">
            <div className="availability-legend-color availability-legend-disabled"></div>
            <span>Indisponible</span>
          </div>
        </div>

        <div className="availability-timetable">
          <div className="availability-timetable-inner">
            <div className="availability-timetable-grid">
              <div className="availability-timetable-header">
                <div className="availability-timetable-header-cell">Horaires</div>
                {visibleSlots.map((slot, index) => {
                  const isHidden = maxVisibleRows && !showAll && index >= maxVisibleRows;
                  return (
                    <div
                      key={slot.label}
                      className={cn(
                        'availability-timetable-time',
                        isHidden && 'availability-timeslot-hidden',
                      )}
                    >
                      <div className="availability-timetable-time-text">{slot.label}</div>
                    </div>
                  );
                })}
                <div className="availability-timetable-time">
                  <div className="availability-timetable-time-text"></div>
                </div>
              </div>

              {DAYS.map((day) => (
                <div key={day.value} className="availability-day-column">
                  <div className="availability-day-header">{day.short}</div>

                  {visibleSlots.map((timeSlot, index) => {
                    const isHidden = maxVisibleRows && !showAll && index >= maxVisibleRows;
                    const isChecked = isCellChecked(day.value, timeSlot);
                    const cellId = `availability-${day.value}-${timeSlot.label}`;
                    return (
                      <div
                        key={timeSlot.label}
                        className={cn(
                          'availability-timeslot',
                          isHidden && 'availability-timeslot-hidden',
                        )}
                      >
                        <input
                          type="checkbox"
                          id={cellId}
                          checked={isChecked}
                          onChange={() => handleCellClick(day.value, timeSlot)}
                          disabled={disabled}
                          className="availability-timeslot-checkbox"
                          aria-label={`${day.label} ${timeSlot.label}`}
                        />
                        <label
                          htmlFor={cellId}
                          className={cn(
                            'availability-timeslot-inner',
                            isChecked
                              ? 'availability-timeslot-checked'
                              : 'availability-timeslot-unchecked',
                            disabled && 'availability-timeslot-disabled',
                          )}
                          title={getCellTitle(isChecked)}
                        >
                          <div className="availability-timeslot-status">
                            {isChecked ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : (
                              <Clock className="w-3 h-3" />
                            )}
                          </div>
                        </label>
                      </div>
                    );
                  })}

                  <div className="availability-timeslot">
                    <button
                      type="button"
                      onClick={() => handleSelectCol(day.value)}
                      disabled={disabled}
                      className={cn(
                        'availability-select-btn',
                        isColAllSelected(day.value) && 'availability-select-btn-active',
                      )}
                      title={
                        isColAllSelected(day.value)
                          ? `Tout désélectionner (${day.label})`
                          : `Tout sélectionner (${day.label})`
                      }
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}

              <div className="availability-day-column">
                <div className="availability-day-header">→ Tout</div>

                {visibleSlots.map((timeSlot, index) => {
                  const isHidden = maxVisibleRows && !showAll && index >= maxVisibleRows;
                  return (
                    <div
                      key={timeSlot.label}
                      className={cn(
                        'availability-timeslot',
                        isHidden && 'availability-timeslot-hidden',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => handleSelectRow(timeSlot)}
                        disabled={disabled}
                        className={cn(
                          'availability-select-btn',
                          isRowAllSelected(timeSlot) && 'availability-select-btn-active',
                        )}
                        title={
                          isRowAllSelected(timeSlot)
                            ? `Tout désélectionner (${timeSlot.label})`
                            : `Tout sélectionner (${timeSlot.label})`
                        }
                      >
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}

                <div className="availability-timeslot">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    disabled={disabled}
                    className={cn(
                      'availability-select-btn availability-select-all-corner',
                      isAllSelected() && 'availability-select-btn-active',
                    )}
                    title={isAllSelected() ? 'Tout désélectionner' : 'Tout sélectionner'}
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {!!(maxVisibleRows && visibleSlots.length > maxVisibleRows) && (
            <div className="availability-show-more">
              {showAll ? (
                <button
                  type="button"
                  onClick={() => setShowAll(false)}
                  className="availability-show-more-btn"
                  disabled={disabled}
                >
                  <ChevronUp className="w-4 h-4" />
                  <span>Réduire</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="availability-show-more-btn"
                  disabled={disabled}
                >
                  <ChevronDown className="w-4 h-4" />
                  <span>
                    Afficher la suite ({visibleSlots.length - (maxVisibleRows ?? 0)} créneaux
                    masqués)
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
